import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { api } from '../services/api';
import { useAuth } from './AuthContext';

export interface Advertiser {
  advertiser_id: string;
  display_name: string;
  access_role?: string;
  balance?: number;
  billing_currency?: string;
  has_external_integration?: boolean;
  // True when the client has media/research spend in the last 2 months OR was
  // created in the last 30 days — controls whether it shows in the switcher's
  // default (no-search) list. Others are reachable via search only.
  in_quicklist?: boolean;
}

export const ALL_CLIENTS: Advertiser = {
  advertiser_id: '__all__',
  display_name: 'All Clients',
};

interface AdvertiserContextType {
  advertisers: Advertiser[];
  selectedAdvertiser: Advertiser;
  selectedAdvertiserId: string;  // '__all__' or real id
  isAllClients: boolean;
  setSelectedAdvertiserId: (id: string) => void;
  isLoading: boolean;
}

const AdvertiserContext = createContext<AdvertiserContextType | undefined>(undefined);

const STORAGE_PREFIX = 'sense_selected_advertiser_';

export const AdvertiserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const storageKey = user ? `${STORAGE_PREFIX}${user.id}` : '';
  const [advertisers, setAdvertisers] = useState<Advertiser[]>([]);
  // Read the saved id synchronously on first render so consumers don't
  // briefly observe the default '__all__' before the saved value is
  // applied (which used to race fetches against an unfiltered list).
  const initialSelectedId = (() => {
    try {
      // We don't know the userId yet on the very first render — but if
      // we ever set the per-user key, it survives across reloads under
      // the same browser anyway, so check all keys.
      const keys = Object.keys(localStorage).filter(k => k.startsWith(STORAGE_PREFIX));
      if (keys.length === 1) return localStorage.getItem(keys[0]) || '__all__';
    } catch {}
    return '__all__';
  })();
  const [selectedId, setSelectedId] = useState<string>(initialSelectedId);
  const [isLoading, setIsLoading] = useState(true);
  const [selectionRestored, setSelectionRestored] = useState<boolean>(initialSelectedId !== '__all__');

  // Load saved selection for current user (overrides the synchronous
  // best-effort read above once we know who the user is).
  useEffect(() => {
    if (storageKey) {
      setSelectedId(localStorage.getItem(storageKey) || '__all__');
      setSelectionRestored(true);
    }
  }, [storageKey]);

  const loadAdvertisers = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setAdvertisers([]);
      setSelectedId('__all__');
      setIsLoading(false);
      return;
    }

    try {
      const resp = await api.get('/api/sense/advertisers?active_only=false&status=active');
      const list: Advertiser[] = (resp.data.advertisers || []).map((a: any) => ({
        advertiser_id: a.advertiser_id,
        display_name: a.display_name || `Advertiser ${a.advertiser_id}`,
        balance: a.balance != null ? parseFloat(a.balance) : undefined,
        billing_currency: a.billing_currency || 'EUR',
        has_external_integration: !!a.has_external_integration,
        in_quicklist: !!a.in_quicklist,
      }));
      list.sort((a, b) => a.display_name.localeCompare(b.display_name));
      setAdvertisers(list);
    } catch (e) {
      console.error('Failed to load advertisers', e);
      setAdvertisers([]);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, user]);

  useEffect(() => { loadAdvertisers(); }, [loadAdvertisers]);

  // After advertisers load: auto-select if exactly 1, resolve legacy ids to
  // their USD twin, otherwise reset to __all__ if the selected id is invalid.
  useEffect(() => {
    if (advertisers.length === 0) return;
    if (advertisers.length === 1) {
      // Single client — auto-select
      const id = advertisers[0].advertiser_id;
      setSelectedId(id);
      if (storageKey) localStorage.setItem(storageKey, id);
    } else if (selectedId !== '__all__' && !advertisers.find(a => a.advertiser_id === selectedId)) {
      // Selected ID is not in the current list. It might be a legacy EUR
      // predecessor — try to resolve to its USD twin via the backend.
      api.get(`/api/sense/advertisers/${selectedId}/resolve`)
        .then((resp) => {
          const resolvedId = resp.data?.advertiser_id;
          if (
            resp.data?.redirected &&
            resolvedId &&
            advertisers.find((a) => a.advertiser_id === resolvedId)
          ) {
            setSelectedId(resolvedId);
            if (storageKey) localStorage.setItem(storageKey, resolvedId);
          } else {
            setSelectedId('__all__');
            if (storageKey) localStorage.setItem(storageKey, '__all__');
          }
        })
        .catch(() => {
          setSelectedId('__all__');
          if (storageKey) localStorage.setItem(storageKey, '__all__');
        });
    }
  }, [advertisers]); // eslint-disable-line react-hooks/exhaustive-deps

  const setSelectedAdvertiserIdFn = useCallback((id: string) => {
    setSelectedId(id);
    if (storageKey) localStorage.setItem(storageKey, id);
  }, [storageKey]);

  const isAllClients = selectedId === '__all__';
  const selectedAdvertiser = advertisers.find(a => a.advertiser_id === selectedId) || ALL_CLIENTS;

  return (
    <AdvertiserContext.Provider value={{
      advertisers,
      selectedAdvertiser,
      selectedAdvertiserId: selectedId,
      isAllClients,
      setSelectedAdvertiserId: setSelectedAdvertiserIdFn,
      // Treat the context as "loading" until both the advertisers list
      // has loaded and the saved selection has been restored from
      // localStorage. Otherwise consumers can fire a fetch with the
      // default '__all__' before the saved id is applied.
      isLoading: isLoading || !selectionRestored,
    }}>
      {children}
    </AdvertiserContext.Provider>
  );
};

export const useAdvertiser = (): AdvertiserContextType => {
  const context = useContext(AdvertiserContext);
  if (!context) {
    throw new Error('useAdvertiser must be used within an AdvertiserProvider');
  }
  return context;
};
